import React, { useState } from 'react';

import { MUSIXMATCH_BRAND_IMAGE, MUSIXMATCH_BRAND_IMAGE_HEIGHT } from '../../../_helpers/lyricsBrand.js';
import { useLocale } from '../../../contexts/LocaleContext.jsx';

/*
 * The credit Musixmatch's licence requires wherever a provider lyrics body is
 * on screen (ENG-415), shared by the performer's stand on the streamer page
 * and the sing-along panel on the watch page.
 *
 * Both parts are unconditional for a provider answer: an answer that arrives
 * without a copyright string or without a backlink is a backend defect, and
 * the right response to it is an unlinked credit and a generic copyright line
 * rather than lyrics with no attribution at all, which is the one outcome the
 * licence forbids.
 *
 * The checklist asks for the credit as the brand *image* from Musixmatch's
 * press kit, which is not a file we have — see `_helpers/lyricsBrand.js` for
 * what that leaves unmet and where it is tracked. The markup here is written
 * image-first regardless: set the constant and the image renders, with the
 * credit sentence as its `alt` and the text we show today as its fallback if
 * the file cannot be loaded.
 *
 * `variant` is the class-name stem, because the two surfaces are styled
 * nothing alike — a card under a video player, and a lyric stand sized for
 * reading at arm's length — while the markup and the obligation are the same.
 *
 * `brandImage` and `brandImageHeight` default to the constants; callers leave
 * them alone. They are props so a test can render the image branch without
 * re-importing the module under a mock (ENG-477).
 */

const LyricsAttribution = ({
  copyright = null,
  backlinkUrl = null,
  variant = 'stream-lyrics',
  brandImage = MUSIXMATCH_BRAND_IMAGE,
  brandImageHeight = MUSIXMATCH_BRAND_IMAGE_HEIGHT,
}) => {
  const { t } = useLocale();
  // A 404 or a blocked request on the brand file must not cost us the credit
  // altogether: the text stands in, which is what renders while we have no
  // asset at all.
  const [imageFailed, setImageFailed] = useState(false);

  const credit = t('stream.lyrics.poweredBy');
  const brand = brandImage && !imageFailed ? (
    <img
      className={`${variant}-brand-image`}
      src={brandImage}
      alt={credit}
      height={brandImageHeight}
      onError={() => setImageFailed(true)}
    />
  ) : (
    <span className={`${variant}-brand-text`}>{credit}</span>
  );

  return (
    <div className={`${variant}-attribution`}>
      {/* The provider's own copyright line when it sent one. When it did not,
          the words are still theirs and still need saying so: the generic
          credit is the floor, never nothing. */}
      <p className={`${variant}-copyright`}>{copyright || t('stream.lyrics.copyrightFallback')}</p>
      {backlinkUrl ? (
        <a className={`${variant}-brand`} href={backlinkUrl} target="_blank" rel="noopener noreferrer">
          {brand}
        </a>
      ) : (
        <span className={`${variant}-brand`}>{brand}</span>
      )}
    </div>
  );
};

export default LyricsAttribution;
